import Component from '@glimmer/component';
import DocumentReviewCard from 'garaje/components/visitors/document-review-card';
import type { DocumentBusyKind, DocumentReviewMode } from 'garaje/components/visitors/document-review-card';
import type CustomUserDocumentTemplateModel from 'garaje/models/custom-user-document-template';
import type UserDocumentTemplateConfigurationModel from 'garaje/models/user-document-template-configuration';
import type VisitorDocumentModel from 'garaje/models/visitor-document';
import type { VisitType } from 'garaje/utils/visitor-documents';

interface DocumentRequirement {
  key: string;
  template: CustomUserDocumentTemplateModel | undefined;
  configuration: UserDocumentTemplateConfigurationModel | undefined;
  document: VisitorDocumentModel | undefined;
  isUploaded: boolean;
}

interface Signature {
  Args: {
    requiredTemplates?: CustomUserDocumentTemplateModel[];
    configurations?: UserDocumentTemplateConfigurationModel[];
    documents?: VisitorDocumentModel[];
    onStatusChange?: () => void;
    /**
     * 'review' (default) renders uploaded docs; 'create' renders a self-driving upload card per
     * required template; 'manage' renders one editable card per required template (matched to its
     * uploaded doc when present) for editing an existing invite's documents.
     */
    mode?: DocumentReviewMode;
    /** Create mode only — passed to each card for the upload link. */
    locationId?: string | null;
    /** Create mode only — the selected flow, passed to each card for confirm. */
    flowId?: string | null;
    /** Create mode only — bubbles each card's draft id up to the spine for invite-create. */
    onDraftChange?: (templateId: string, draftId: string | null) => void;
    /** Create mode only — bubbles each card's busy state (upload vs field-values save) up to the spine. */
    onUploadStateChange?: (templateId: string, isUploading: boolean, kind: DocumentBusyKind) => void;
    /** Manage mode only — which visit (invite XOR entry) each card's direct upload attaches to. */
    visitType?: VisitType;
    /** Manage mode only — the id of the visit each card's upload attaches to. */
    visitId?: string;
  };
  Element: HTMLDivElement;
}

export default class DocumentReviewList extends Component<Signature> {
  get isCreateMode(): boolean {
    return this.args.mode === 'create';
  }

  get isManageMode(): boolean {
    return this.args.mode === 'manage';
  }

  private get configByTemplateId(): Map<string, UserDocumentTemplateConfigurationModel> {
    /* eslint-disable ember/use-ember-get-and-set */
    const configByTemplateId = new Map<string, UserDocumentTemplateConfigurationModel>();
    for (const config of this.args.configurations ?? []) {
      const templateId = config.belongsTo('userDocumentTemplate').id();
      if (templateId) configByTemplateId.set(templateId, config);
    }
    /* eslint-enable ember/use-ember-get-and-set */
    return configByTemplateId;
  }

  get requirements(): DocumentRequirement[] {
    /* eslint-disable ember/use-ember-get-and-set */
    const templates = this.args.requiredTemplates ?? [];
    const configByTemplateId = this.configByTemplateId;

    // Create mode: one upload card per required template — there are no uploaded
    // docs yet, so skip the doc-matching the review path does. Each card self-drives
    // its own upload/parse via the draft-documents service.
    if (this.isCreateMode) {
      return templates.map((template) => ({
        key: `t-${template.id}`,
        template,
        configuration: configByTemplateId.get(template.id),
        document: undefined,
        isUploaded: false,
      }));
    }

    // Manage mode: one editable card per required template, matched to its uploaded doc when
    // present (undefined otherwise), THEN any extra uploaded docs appended — mirroring the review
    // branch below — so a second doc for the same template (e.g. a re-upload) or a doc whose
    // template is no longer required stays visible and deletable instead of silently hidden.
    if (this.isManageMode) {
      const managedDocs = this.args.documents ?? [];
      const requiredById = new Map(templates.map((template) => [template.id, template]));
      const matched = new Set<string>();
      const requirements: DocumentRequirement[] = templates.map((template) => {
        const doc = managedDocs.find((d) => d.belongsTo('userDocumentTemplate').id() === template.id);
        if (doc) matched.add(doc.id);
        return {
          key: `t-${template.id}`,
          template,
          configuration: configByTemplateId.get(template.id),
          document: doc,
          isUploaded: Boolean(doc),
        };
      });
      for (const doc of managedDocs) {
        if (!matched.has(doc.id)) {
          // A re-upload for a still-required template carries that template's configuration so its
          // on_expiration approval gate still applies to the appended card; a doc whose template is
          // no longer required has neither template nor configuration.
          const templateId = doc.belongsTo('userDocumentTemplate').id();
          const reqTemplate = templateId ? requiredById.get(templateId) : undefined;
          requirements.push({
            key: `d-${doc.id}`,
            template: reqTemplate,
            configuration: reqTemplate ? configByTemplateId.get(reqTemplate.id) : undefined,
            document: doc,
            isUploaded: true,
          });
        }
      }
      return requirements;
    }

    const docs = this.args.documents ?? [];
    const matchedDocIds = new Set<string>();

    // Match each required template to the first uploaded document (if any).
    // If multiple docs share the same template (e.g. re-upload after denial),
    // only the first is matched here — extras appear at the end of the list.
    const requirements: DocumentRequirement[] = templates.map((template) => {
      const doc = docs.find((d) => d.belongsTo('userDocumentTemplate').id() === template.id);
      if (doc) matchedDocIds.add(doc.id);
      const configuration = configByTemplateId.get(template.id);
      return { key: `t-${template.id}`, template, configuration, document: doc, isUploaded: !!doc };
    });

    /* eslint-enable ember/use-ember-get-and-set */
    // Append extra uploaded docs that don't match any required template
    for (const doc of docs) {
      if (!matchedDocIds.has(doc.id)) {
        requirements.push({
          key: `d-${doc.id}`,
          template: undefined,
          configuration: undefined,
          document: doc,
          isUploaded: true,
        });
      }
    }

    return requirements;
  }

  <template>
    <div data-test-document-review-list ...attributes>
      {{#each this.requirements key="key" as |req|}}
        <DocumentReviewCard
          @document={{req.document}}
          @template={{req.template}}
          @configuration={{req.configuration}}
          @isUploaded={{req.isUploaded}}
          @onStatusChange={{@onStatusChange}}
          @mode={{@mode}}
          @locationId={{@locationId}}
          @flowId={{@flowId}}
          @onDraftChange={{@onDraftChange}}
          @onUploadStateChange={{@onUploadStateChange}}
          @visitType={{@visitType}}
          @visitId={{@visitId}}
        />
      {{/each}}

      {{#unless this.requirements.length}}
        <p class="text-carbon-50 text-center py-8" data-test-no-documents>
          No documents required for this visitor type.
        </p>
      {{/unless}}
    </div>
  </template>
}
